import React, { useState } from "react";
import Icons from "../icons";
import ConfirmModal from "./ConfirmModal";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* What deleting asks before it is done, for an Artifact or an execution
   alike (ConfirmModal adds "This action cannot be undone."). */
export const DELETE_MESSAGE = "Are you sure you want to delete this item?";
/* A Test goes with its Artifact and executions, named as the Requirement
   Board names what it deletes. It is logged (one entry, for the Test, with
   its content) only when the footer's box is ticked. */
export const TEST_WARNING =
  "This will also permanently delete its Artifact and all its executions, including their results and evidence.";
export const KEEP_LOG = "Store this deletion in the Log";

/* A Trash icon that deletes, after a warning, an App Page Test with its
   Artifact and executions (`what` "test", by its `testKey`), only a Test's
   Artifact (`what` "artifact", by its `testKey`) or one execution (`what`
   "execution", by its `executionKey`). Each delete is logged by the API
   (test/artifact/execution.deleted), with who did it. `onDeleted` runs once
   it is gone. */
export default function AppPageDeleteButton({
  what,
  testKey = "",
  artifactKey = "",
  executionKey = "",
  name = "",
  userkey,
  disabled = false,
  disabledReason = "",
  onDeleted,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [keepLog, setKeepLog] = useState(false);
  const test = `app-pages/tests/${encodeURIComponent(testKey)}`;
  const { label, path } = {
    test: { label: `Delete ${testKey}`, path: test },
    artifact: { label: `Delete ${artifactKey || `the Artifact of ${testKey}`}`, path: `${test}/artifact` },
    execution: { label: `Delete ${executionKey}`, path: `app-pages/executions/${encodeURIComponent(executionKey)}` },
  }[what];

  const remove = async () => {
    setBusy(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (userkey) params.set("userkey", userkey);
      if (what === "test" && keepLog) params.set("log", "true");
      const query = params.toString() ? `?${params}` : "";
      const res = await fetch(`${API_BASE_URL}${path}${query}`, { method: "DELETE" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body?.detail === "string" ? body.detail : `HTTP ${res.status}`);
      setOpen(false);
      onDeleted?.(body.data);
    } catch (e) {
      setError(e.message || "It could not be deleted.");
    } finally {
      setBusy(false);
    }
  };

  const message =
    what === "test"
      ? `Are you sure you want to delete ${testKey}${name ? ` "${name}"` : ""}?\n${TEST_WARNING}`
      : DELETE_MESSAGE;
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setError("");
          setKeepLog(false);
          setOpen(true);
        }}
        disabled={disabled}
        title={disabled && disabledReason ? disabledReason : label}
        aria-label={label}
        className="inline-flex h-[30px] w-[30px] flex-none items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
      >
        <Icons.Trash className="h-4 w-4" />
      </button>
      {open && (
        /* The dialog sits inside clickable cards: its clicks stay here. */
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmModal
            title={label}
            icon={Icons.Trash}
            message={
              error ? (
                <>
                  <p>{message}</p>
                  <p role="alert" className="text-[14px] text-rose-700">
                    Not deleted: {error}
                  </p>
                </>
              ) : (
                message
              )
            }
            irreversible
            option={what === "test" ? { label: KEEP_LOG, checked: keepLog, onChange: setKeepLog } : null}
            confirmLabel="Delete"
            busyLabel="Deleting…"
            busy={busy}
            onCancel={() => setOpen(false)}
            onConfirm={remove}
          />
        </div>
      )}
    </>
  );
}
